import moment from "moment";
import type {
  AttributeGroupSummary,
  AttributeSchema,
  AttributeValue,
  CollectionSchema,
  CollectionValueRowSchema,
  PIMProduct,
  TemplateTabData,
} from "../../../../../types/types.PIM";

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*?>/i;

function looksLikeHtml(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

/**
 * Convert Quill/HTML rich text to a single-line plain string for CSV/compare.
 * Uses the DOM when available so entities (&nbsp;, &amp;) and nested tags
 * are handled; falls back to a regex strip in non-browser environments.
 */
export function stripHtmlTags(html: string): string {
  if (!html) return "";
  const withBreaks = html
    .replace(/<(br|hr)\s*\/?>/gi, " ")
    .replace(/<\/(p|div|h[1-6]|li|tr|blockquote|pre)>/gi, " ");
  let text = withBreaks;
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    text = doc.body.textContent || "";
  } else {
    text = withBreaks.replace(/<[^>]*>/g, " ");
  }
  return text.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

export const MISSING_VALUE_DISPLAY = "--";
export const MIN_COMPARE_PRODUCTS = 2;
export const MAX_COMPARE_PRODUCTS = 4;

export function isAttributeNotApplicable(
  attribute: { is_not_applicable?: boolean } | undefined
): boolean {
  return Boolean(attribute?.is_not_applicable);
}

/** Role-view visibility. Missing flag is treated as visible (API default is true). */
export function isAttributeVisible(
  attribute: { is_visible?: boolean } | undefined
): boolean {
  return attribute?.is_visible !== false;
}

export function isCollectionVisible(
  collection: { is_visible?: boolean } | undefined
): boolean {
  return collection?.is_visible !== false;
}

function isAttributeShownInCompare<
  T extends { is_not_applicable?: boolean; is_visible?: boolean }
>(attribute: T | undefined): attribute is T {
  return (
    attribute != null &&
    !isAttributeNotApplicable(attribute) &&
    isAttributeVisible(attribute)
  );
}

/** Same key as ProductsList — keep selection in sync when removing from compare. */
export const SESSION_SELECTED_ROWS_KEY = "selectedRows";

export type CompareSectionKind = "group" | "collection";

export type CompareSection = {
  id: string;
  name: string;
  display_name: string;
  kind: CompareSectionKind;
};

const EXCLUDED_TAB_TYPES = new Set(["documents", "seo", "sku"]);
const EXCLUDED_GROUP_NAMES = new Set(["seo"]);
const EXCLUDED_COLLECTION_OBJECT_TYPES = new Set(["skus"]);

export function getCompareDisableReason(
  selectedCount: number,
  templateIds: (string | null | undefined)[],
  t: (key: string) => string
): string | null {
  if (selectedCount > MAX_COMPARE_PRODUCTS) {
    return t("You can compare a maximum of 4 Products");
  }
  if (selectedCount < MIN_COMPARE_PRODUCTS) {
    return t("Select at least 2 products to compare");
  }
  const knownTemplateIds = templateIds.filter(
    (id): id is string => typeof id === "string" && id.length > 0
  );
  // If we know templates for some/all selections, they must match.
  // Unknown templates (e.g. select-all / condensed summary) are validated
  // on the compare page after full product fetch.
  if (knownTemplateIds.length > 0 && new Set(knownTemplateIds).size > 1) {
    return t("Products must be of the same template to compare");
  }
  return null;
}

/**
 * Build ordered compare sections from template layout, excluding Assets, SEO,
 * SKUs, and List Prices (pricing is not part of layout).
 * Pass all compared products so groups/collections that are N/A or not visible
 * on every product are omitted, matching product detail for seller users.
 */
export function getCompareSections(
  product: PIMProduct,
  allProducts: PIMProduct[] = [product]
): CompareSection[] {
  const layout = product.product_schema.layout ?? [];
  const sections: CompareSection[] = [];
  const seen = new Set<string>();

  const appendFromLayout = (tabs: TemplateTabData[]) => {
    const orderedTabs = [...tabs].sort(
      (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );
    for (const tab of orderedTabs) {
      if (!tab.is_enabled || EXCLUDED_TAB_TYPES.has(tab.tab_type)) {
        continue;
      }
      for (const item of tab.items ?? []) {
        const key = `${item.type}:${item.name.toLowerCase()}`;
        if (seen.has(key)) continue;

        if (item.type === "group") {
          const group = product.product_schema.groups.find(
            (g) => g.name.toLowerCase() === item.name.toLowerCase()
          );
          if (
            !group ||
            EXCLUDED_GROUP_NAMES.has(group.name.toLowerCase()) ||
            !group.attributes?.length
          ) {
            continue;
          }
          seen.add(key);
          sections.push({
            id: group.id,
            name: group.name,
            display_name: group.display_name || group.name,
            kind: "group",
          });
        } else if (item.type === "collection") {
          const collection = product.product_schema.collections.find(
            (c) => c.name.toLowerCase() === item.name.toLowerCase()
          );
          if (
            !collection ||
            EXCLUDED_COLLECTION_OBJECT_TYPES.has(collection.object_type) ||
            !collection.columns?.length
          ) {
            continue;
          }
          seen.add(key);
          sections.push({
            id: collection.id,
            name: collection.name,
            display_name: collection.display_name || collection.name,
            kind: "collection",
          });
        }
      }
    }
  };

  appendFromLayout(layout);

  // Fallback: include remaining visible groups/collections not referenced in layout
  for (const group of product.product_schema.groups) {
    const key = `group:${group.name.toLowerCase()}`;
    if (
      seen.has(key) ||
      EXCLUDED_GROUP_NAMES.has(group.name.toLowerCase()) ||
      !group.attributes?.length
    ) {
      continue;
    }
    seen.add(key);
    sections.push({
      id: group.id,
      name: group.name,
      display_name: group.display_name || group.name,
      kind: "group",
    });
  }

  for (const collection of product.product_schema.collections) {
    const key = `collection:${collection.name.toLowerCase()}`;
    if (
      seen.has(key) ||
      EXCLUDED_COLLECTION_OBJECT_TYPES.has(collection.object_type) ||
      !collection.columns?.length
    ) {
      continue;
    }
    seen.add(key);
    sections.push({
      id: collection.id,
      name: collection.name,
      display_name: collection.display_name || collection.name,
      kind: "collection",
    });
  }

  const comparedProducts = allProducts.length ? allProducts : [product];
  return sections.filter((section) => {
    if (section.kind === "group") {
      return getAlignedGroupAttributes(comparedProducts, section.name).length > 0;
    }
    return getAlignedCollectionColumns(comparedProducts, section.name).length > 0;
  });
}

export function findGroupByName(
  product: PIMProduct,
  name: string
): AttributeGroupSummary | undefined {
  return product.product_schema.groups.find(
    (g) => g.name.toLowerCase() === name.toLowerCase()
  );
}

export function findCollectionByName(
  product: PIMProduct,
  name: string
): CollectionSchema | undefined {
  return product.product_schema.collections.find(
    (c) => c.name.toLowerCase() === name.toLowerCase()
  );
}

export function findAttributeByName(
  group: AttributeGroupSummary | undefined,
  attributeName: string
): AttributeSchema | undefined {
  return group?.attributes.find(
    (a) => a.name.toLowerCase() === attributeName.toLowerCase()
  );
}

/** Union of attribute definitions across products for a named group (aligned by attribute name). */
export function getAlignedGroupAttributes(
  products: PIMProduct[],
  groupName: string
): AttributeSchema[] {
  const byName = new Map<string, AttributeSchema>();
  for (const product of products) {
    const group = findGroupByName(product, groupName);
    for (const attr of group?.attributes ?? []) {
      if (!byName.has(attr.name.toLowerCase())) {
        byName.set(attr.name.toLowerCase(), attr);
      }
    }
  }
  // Preserve order from the first product that has the group
  const firstGroup = products
    .map((p) => findGroupByName(p, groupName))
    .find(Boolean);
  if (!firstGroup) return Array.from(byName.values());

  const ordered: AttributeSchema[] = [];
  const used = new Set<string>();
  for (const attr of firstGroup.attributes) {
    const key = attr.name.toLowerCase();
    const def = byName.get(key);
    if (def) {
      ordered.push(def);
      used.add(key);
    }
  }
  for (const [key, attr] of byName) {
    if (!used.has(key)) ordered.push(attr);
  }
  return ordered.filter((attrDef) =>
    products.some((product) =>
      isAttributeShownInCompare(
        getProductGroupAttribute(product, groupName, attrDef.name)
      )
    )
  );
}

/** Resolve the attribute instance for a product in a named group (by attribute name). */
export function getProductGroupAttribute(
  product: PIMProduct,
  groupName: string,
  attributeName: string
): AttributeSchema | undefined {
  return findAttributeByName(findGroupByName(product, groupName), attributeName);
}

/**
 * Build comparison rows for a group: one row per attribute, values aligned to products[].
 * Values are taken from each product independently so columns cannot share state.
 */
export function getGroupCompareRows(
  products: PIMProduct[],
  groupName: string
): Array<{
  attributeName: string;
  displayName: string;
  attributesByProductId: Record<string, AttributeSchema | undefined>;
}> {
  const aligned = getAlignedGroupAttributes(products, groupName);
  return aligned.map((attrDef) => {
    const attributesByProductId: Record<string, AttributeSchema | undefined> =
      {};
    for (const product of products) {
      // Clone so React/display components cannot accidentally share references
      // across columns (attribute ids are identical across same-template products).
      const source = getProductGroupAttribute(
        product,
        groupName,
        attrDef.name
      );
      attributesByProductId[product.id] = isAttributeShownInCompare(source)
        ? {
            ...source,
            values: source.values ? source.values.map((v) => ({ ...v })) : [],
          }
        : undefined;
    }
    return {
      attributeName: attrDef.name,
      displayName: attrDef.display_name || attrDef.name,
      attributesByProductId,
    };
  });
}

export function attributeValueToPlainText(
  attribute: AttributeSchema | undefined
): string {
  if (!attribute || attribute.is_not_applicable || attribute.is_visible === false) {
    return MISSING_VALUE_DISPLAY;
  }
  const values = attribute.values ?? [];
  if (!values.length) {
    return MISSING_VALUE_DISPLAY;
  }

  switch (attribute.input_type) {
    case "date": {
      const raw = values[0]?.value;
      if (raw == null || raw === " " || raw === "") return MISSING_VALUE_DISPLAY;
      return moment(raw as string).format("MMM DD, YYYY");
    }
    case "toggle": {
      const raw = values[0]?.value;
      if (raw == null) return MISSING_VALUE_DISPLAY;
      return raw ? "On" : "Off";
    }
    case "multi_select":
      return (
        values
          .map((v) => v.enum_variant?.variant ?? String(v.value ?? ""))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    case "multiline_entry": {
      const raw = values[0]?.value;
      if (raw == null || raw === "" || raw === " ") return MISSING_VALUE_DISPLAY;
      const text = stripHtmlTags(String(raw));
      return text || MISSING_VALUE_DISPLAY;
    }
    case "form_field": {
      const raw = values[0]?.value;
      if (raw == null || raw === "" || raw === " ") return MISSING_VALUE_DISPLAY;
      const asString = String(raw);
      const text = looksLikeHtml(asString) ? stripHtmlTags(asString) : asString;
      return text.trim() || MISSING_VALUE_DISPLAY;
    }
    case "product_reference": {
      return (
        values
          .map((v) => formatReferenceName(v))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    }
    case "object_reference": {
      return (
        values
          .map((v) => formatReferenceName(v))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    }
    case "user": {
      return (
        values
          .map((v) => formatUserValue(v))
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    }
    case "image":
      return values.length ? `${values.length} file(s)` : MISSING_VALUE_DISPLAY;
    case "document":
      return (
        values
          .map(getDocumentAssetId)
          .filter(Boolean)
          .join("; ") || MISSING_VALUE_DISPLAY
      );
    default: {
      if (values.length > 1) {
        return (
          values
            .map((v) => stringifyPrimitiveValue(v))
            .filter((s) => s && s !== " ")
            .join("; ") || MISSING_VALUE_DISPLAY
        );
      }
      const text = stringifyPrimitiveValue(values[0]);
      return !text || text === " " ? MISSING_VALUE_DISPLAY : text;
    }
  }
}

type UserLike = {
  firstname?: string | null;
  lastname?: string | null;
  email_address?: string | null;
};

function isUserLike(value: unknown): value is UserLike {
  return (
    !!value &&
    typeof value === "object" &&
    ("firstname" in value || "lastname" in value || "email_address" in value)
  );
}

function formatUserLike(user: UserLike): string {
  const fromParts = [user.firstname, user.lastname]
    .filter((part): part is string => Boolean(part && String(part).trim()))
    .map((part) => String(part).trim())
    .join(" ");
  if (fromParts) return fromParts;
  return user.email_address?.trim() || "";
}

function formatUserValue(value: AttributeValue): string {
  if (value.user) return formatUserLike(value.user);
  if (isUserLike(value.value)) return formatUserLike(value.value);
  return "";
}

function formatReferenceName(value: AttributeValue): string {
  if (value.object_ref?.name) return value.object_ref.name;
  if (value.product_ref?.name) return value.product_ref.name;
  const raw = value.value;
  if (raw && typeof raw === "object" && "name" in raw) {
    return String((raw as { name?: string }).name ?? "");
  }
  return typeof raw === "string" ? raw : "";
}

function formatComplexValue(raw: object): string {
  if (isUserLike(raw)) return formatUserLike(raw);
  if ("name" in raw && (raw as { name?: unknown }).name != null) {
    return String((raw as { name: unknown }).name);
  }
  if (
    "display_text" in raw &&
    (raw as { display_text?: unknown }).display_text != null
  ) {
    return String((raw as { display_text: unknown }).display_text);
  }
  // Never surface raw JSON / bare IDs as compare labels.
  return "";
}

function stringifyPrimitiveValue(value: AttributeValue | undefined): string {
  if (!value) return "";
  if (value.user) return formatUserLike(value.user);
  if (value.object_ref?.name) return value.object_ref.name;
  if (value.product_ref?.name) return value.product_ref.name;

  const raw = value.value;
  if (raw == null) return value.enum_variant?.variant ?? "";
  if (typeof raw === "boolean") return raw ? "On" : "Off";
  if (typeof raw === "object") return formatComplexValue(raw);
  const text = String(raw);
  return looksLikeHtml(text) ? stripHtmlTags(text) : text;
}

function getDocumentAssetId(value: AttributeValue): string {
  const raw = value.value;
  if (raw && typeof raw === "object" && "asset_id" in raw) {
    return String((raw as { asset_id?: string }).asset_id ?? "");
  }
  return "";
}

export function getCollectionRowLabel(
  collection: CollectionSchema,
  row: CollectionValueRowSchema
): string {
  const firstColumn = collection.columns[0];
  if (!firstColumn) return row.row_id;
  const values = (row.values ?? []).filter(
    (v) => v.attribute_id === firstColumn.id
  );
  // Row headers only need a short label — use the first value, not every selection.
  const firstValue = values[0];
  if (!firstValue) return MISSING_VALUE_DISPLAY;
  return attributeValueToPlainText({
    ...firstColumn,
    values: [firstValue] as AttributeValue[],
  });
}

export type AlignedCollectionItem = {
  /** Unique key for map / React (stable even when display labels collide). */
  id: string;
  /** Display label from the first column (may be "--" for missing values). */
  rowKey: string;
  /** Per-product value_row for this aligned item (undefined if product lacks it). */
  rowsByProductId: Record<string, CollectionValueRowSchema | undefined>;
};

function collectionItemAlignmentKey(
  label: string,
  row: CollectionValueRowSchema
): string {
  const normalized = label.trim().toLowerCase();
  // Missing first-column values all stringify to "--"; use row_id so they
  // don't collapse onto one map entry and overwrite each other.
  if (!normalized || normalized === MISSING_VALUE_DISPLAY.toLowerCase()) {
    return `__missing__:${row.row_id}`;
  }
  return normalized;
}

/**
 * Align collection items across products by the first column attribute value.
 * Rows with a missing first-column value stay as separate items (keyed by row_id).
 */
export function getAlignedCollectionItems(
  products: PIMProduct[],
  collectionName: string
): AlignedCollectionItem[] {
  const collections = products.map((p) => ({
    productId: p.id,
    collection: findCollectionByName(p, collectionName),
  }));

  const firstWithColumns = collections.find((c) => c.collection?.columns?.length)
    ?.collection;
  if (!firstWithColumns) return [];

  const order: string[] = [];
  const items = new Map<string, AlignedCollectionItem>();

  for (const { productId, collection } of collections) {
    if (!collection?.value_rows) continue;
    for (const row of collection.value_rows) {
      const label = getCollectionRowLabel(collection, row);
      let mapKey = collectionItemAlignmentKey(label, row);
      let item = items.get(mapKey);

      // Same non-empty label twice on one product — keep both rows.
      if (item && item.rowsByProductId[productId] !== undefined) {
        mapKey = `${mapKey}::${row.row_id}`;
        item = undefined;
      }

      if (!item) {
        item = { id: mapKey, rowKey: label, rowsByProductId: {} };
        items.set(mapKey, item);
        order.push(mapKey);
      }
      item.rowsByProductId[productId] = row;
    }
  }

  return order.map((key) => items.get(key)!);
}

export function getCollectionAttributeForProduct(
  collection: CollectionSchema | undefined,
  row: CollectionValueRowSchema | undefined,
  column: AttributeSchema
): AttributeSchema | undefined {
  if (!collection || !row) return undefined;
  const matchingColumn = collection.columns.find(
    (c) => c.name.toLowerCase() === column.name.toLowerCase()
  );
  if (!matchingColumn || !isAttributeShownInCompare(matchingColumn)) {
    return undefined;
  }

  const values = (row.values ?? []).filter(
    (v) => v.attribute_id === matchingColumn.id
  );

  return {
    ...matchingColumn,
    values: values as AttributeValue[],
  };
}

export function getAlignedCollectionColumns(
  products: PIMProduct[],
  collectionName: string
): AttributeSchema[] {
  const byName = new Map<string, AttributeSchema>();
  for (const product of products) {
    const collection = findCollectionByName(product, collectionName);
    for (const col of collection?.columns ?? []) {
      if (!byName.has(col.name.toLowerCase())) {
        byName.set(col.name.toLowerCase(), col);
      }
    }
  }
  const first = products
    .map((p) => findCollectionByName(p, collectionName))
    .find((c) => c?.columns?.length);
  if (!first) return Array.from(byName.values());

  const ordered: AttributeSchema[] = [];
  const used = new Set<string>();
  for (const col of first.columns) {
    const key = col.name.toLowerCase();
    const def = byName.get(key);
    if (def) {
      ordered.push(def);
      used.add(key);
    }
  }
  for (const [key, col] of byName) {
    if (!used.has(key)) ordered.push(col);
  }
  return ordered.filter((colDef) =>
    products.some((product) => {
      const collection = findCollectionByName(product, collectionName);
      if (!isCollectionVisible(collection)) return false;
      const column = collection?.columns.find(
        (c) => c.name.toLowerCase() === colDef.name.toLowerCase()
      );
      return isAttributeShownInCompare(column);
    })
  );
}
